import { useState } from 'react';
import { ChevronDown, Layers, Plus, Check, Loader2 } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * BrandSwitcher — header dropdown for picking the active brand.
 *
 * Hidden entirely while the tenant has 0 or 1 brand (progressive disclosure).
 * The second a subscriber clicks "+ New Brand", the switcher appears for all
 * subsequent sessions, exposing the agency tier without forcing it on solo
 * subscribers.
 */
export function BrandSwitcher() {
  const { user } = useAuth();
  const { brands, activeBrand, switchBrand, createBrand, loading } = useBrand();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);

  // Hide for super_admins (they aren't tied to a brand) and while loading
  if (!user || user.role === 'super_admin' || loading) return null;

  const visible = brands.length > 1; // Hide entirely with 0-1 brands
  // Always render the create button via the user menu, but the active-brand
  // chip only appears once there are 2+ brands.

  const handleSwitch = async (id) => {
    try {
      await switchBrand(id);
      toast.success(`Switched to ${brands.find(b => b.id === id)?.name}`);
    } catch {
      toast.error('Could not switch brand');
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await createBrand({ name, description: newDesc.trim() });
      toast.success(`Brand "${name}" created`);
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create brand');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {visible && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="hidden md:flex items-center gap-1.5 text-xs h-8 px-2 text-gray-light hover:bg-white/10"
              data-testid="header-brand-switcher"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: activeBrand?.color || '#10B981' }}
              />
              <span className="font-medium max-w-[120px] truncate">{activeBrand?.name || 'Select brand'}</span>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
              <Layers className="h-3 w-3" /> Brands ({brands.length})
            </div>
            <DropdownMenuSeparator />
            {brands.map(b => (
              <DropdownMenuItem
                key={b.id}
                onClick={() => handleSwitch(b.id)}
                className={cn("cursor-pointer flex items-center gap-2", b.id === activeBrand?.id && "bg-emerald-brand/5")}
                data-testid={`brand-option-${b.slug}`}
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: b.color }} />
                <span className="flex-1 truncate">{b.name}</span>
                {b.id === activeBrand?.id && <Check className="h-3 w-3 text-emerald-600" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setCreateOpen(true)}
              className="cursor-pointer text-emerald-600 font-medium"
              data-testid="brand-create-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New brand
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Show "+ Add brand" CTA even when there's only 1 brand — keeps the agency tier discoverable */}
      {!visible && brands.length === 1 && user?.role !== 'super_admin' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="hidden md:flex items-center gap-1.5 text-xs h-8 px-2 text-gray-light hover:bg-white/10"
          data-testid="header-add-first-brand"
          title="Manage multiple brands from one login"
        >
          <Plus className="h-3.5 w-3.5" /> Add brand
        </Button>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a new brand</DialogTitle>
            <DialogDescription>
              Brands let you manage multiple clients or products from one Konnect login.
              Each brand has its own Konnect content queue, brand DNA, and performance metrics.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Brand name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Acme Corp — Premium Line"
                data-testid="new-brand-name"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Short description (optional)</label>
              <Textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="What's special about this brand's voice / audience?"
                rows={2}
                data-testid="new-brand-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!newName.trim() || saving}
              data-testid="new-brand-submit"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
              Create brand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BrandSwitcher;
